// src/components/Hero.jsx
import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { useT } from '@/context/TranslateCtx'

const IMAGES = [
  '/assets/images/hero1.jpg',
  '/assets/images/hero2.jpg',
  '/assets/images/hero3.jpg',
]

export default function Hero() {
  const { t } = useT()
  const [idx, setIdx] = useState(0)

  useEffect(() => {
    const h = setInterval(() => {
      setIdx(i => (i + 1) % IMAGES.length)
    }, 7000)
    return () => clearInterval(h)
  }, [])

  return (
    <section className="relative w-full h-screen overflow-hidden">
      {/* Slider */}
      <AnimatePresence>
        <motion.img
          key={IMAGES[idx]}
          src={IMAGES[idx]}
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: 'easeInOut' }}
        />
      </AnimatePresence>

      {/* Gentle gradient overlay (no blur) */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/30" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full px-6 text-center text-white">
        <motion.h1
          className="text-6xl md:text-7xl lg:text-8xl font-extrabold drop-shadow-2xl"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1 }}
        >
          Kuizy.ai
        </motion.h1>

        <motion.p
          className="mt-4 text-xl md:text-2xl max-w-2xl leading-relaxed text-white/90"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
        >
          {t('hero.subtitle')}
        </motion.p>

        <motion.div
          className="mt-8 flex flex-col sm:flex-row gap-4"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.8 }}
        >
          <a
            href="https://app.kuizy.ai/register"
            className="inline-flex items-center px-8 py-3 text-lg md:text-xl font-semibold rounded-full bg-white text-primary shadow-lg hover:shadow-2xl hover:-translate-y-1 transition"
          >
            {t('hero.start')}
            <ArrowRight size={20} className="ml-2" />
          </a>
          <a
            href="#learn-more"
            className="inline-flex items-center px-8 py-3 text-lg md:text-xl font-medium rounded-full bg-white/20 text-white backdrop-blur-sm hover:bg-white/30 hover:-translate-y-1 transition"
          >
            {t('hero.details')}
          </a>
        </motion.div>
      </div>

      {/* Scroll hint */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/80"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2.2, repeat: Infinity }}
      >
        ↓
      </motion.div>
    </section>
  )
}
